import { type MouseEvent, useMemo, useRef, useState } from "react";
import { isPaletteKey } from "@/model/palette";
import { hexOf } from "@/model/resolve";
import { TOKEN_MAP, type TokenId } from "@/model/tokens";
import { type PreviewTab, useEditor, useShownTheme } from "@/store/editor";
import { cn } from "@/ui/kit/cn";
import { ClaudeCodePreview } from "./claude-code";
import { ContrastView } from "./contrast";
import { CursorAgentPreview } from "./cursor-agent";
import { TermWindow } from "./frame";
import { HerdrPreview } from "./herdr";
import { NeovimPreview } from "./neovim";
import { AnsiGrid, TerminalBody } from "./terminal";
import { previewVars } from "./vars";
import { WeeChatPreview } from "./weechat";
import { WorkspacePreview } from "./workspace";

export const PREVIEW_TABS: { id: PreviewTab; label: string }[] = [
  { id: "workspace", label: "Workspace" },
  { id: "neovim", label: "Neovim" },
  { id: "terminal", label: "Ghostty" },
  { id: "herdr", label: "herdr" },
  { id: "claude", label: "Claude Code" },
  { id: "cursor", label: "Cursor Agent" },
  { id: "weechat", label: "WeeChat" },
  { id: "contrast", label: "Contrast" },
];

function Body({ tab }: { tab: PreviewTab }) {
  switch (tab) {
    case "workspace":
      return <WorkspacePreview />;
    case "neovim":
      return (
        <TermWindow title="nvim lua/themes/init.lua" className="h-full">
          <NeovimPreview />
        </TermWindow>
      );
    case "terminal":
      return (
        <TermWindow title="fish — ~/code/themes" className="h-full" bodyClassName="flex flex-col">
          <TerminalBody />
          <div className="mt-auto p-3">
            <AnsiGrid />
          </div>
        </TermWindow>
      );
    case "herdr":
      return (
        <TermWindow title="herdr" className="h-full">
          <HerdrPreview />
        </TermWindow>
      );
    case "claude":
      return (
        <TermWindow title="claude — ~/ghq/github.com/lnutimura/themes" className="h-full">
          <ClaudeCodePreview />
        </TermWindow>
      );
    case "cursor":
      return (
        <TermWindow title="agent — ~/ghq/github.com/lnutimura/themes" className="h-full">
          <CursorAgentPreview />
        </TermWindow>
      );
    case "weechat":
      return (
        <TermWindow title="weechat" className="h-full">
          <WeeChatPreview />
        </TermWindow>
      );
    case "contrast":
      return <ContrastView />;
  }
}

function labelFor(id: string): string {
  if (id.startsWith("palette.")) return `palette · ${id.slice(8)}`;
  const def = TOKEN_MAP.get(id as TokenId);
  return def ? `${def.group} · ${def.label}` : id;
}

export function Stage() {
  const tab = useEditor((s) => s.previewTab);
  const vision = useEditor((s) => s.vision);
  const inspect = useEditor((s) => s.inspect);
  const select = useEditor((s) => s.select);
  const { theme, resolved, isB } = useShownTheme();
  const style = useMemo(() => previewVars(theme, resolved, vision), [theme, resolved, vision]);
  const [hover, setHover] = useState<{ id: string; x: number; y: number } | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  const tokenAt = (e: MouseEvent) =>
    (e.target as HTMLElement).closest<HTMLElement>("[data-token]")?.dataset.token;

  const onClick = (e: MouseEvent) => {
    if (!inspect && !e.altKey) return;
    const id = tokenAt(e);
    if (!id) return;
    e.preventDefault();
    if (id.startsWith("palette.")) {
      const key = id.slice(8);
      if (isPaletteKey(key)) select({ kind: "palette", key });
    } else if (TOKEN_MAP.has(id as TokenId)) {
      select({ kind: "token", id: id as TokenId });
    }
  };

  const onMove = (e: MouseEvent) => {
    if (!inspect) return setHover(null);
    const id = tokenAt(e);
    const box = ref.current?.getBoundingClientRect();
    if (!id || !box) return setHover(null);
    setHover({ id, x: e.clientX - box.left + 14, y: e.clientY - box.top + 14 });
  };

  return (
    <div
      ref={ref}
      className={cn("relative h-full min-h-0", inspect && "inspecting")}
      style={style}
      onClickCapture={onClick}
      onMouseMove={onMove}
      onMouseLeave={() => setHover(null)}
      data-testid="stage"
    >
      <Body tab={tab} />
      {isB && (
        <div className="pointer-events-none absolute top-3 left-1/2 z-30 -translate-x-1/2 rounded-full bg-accent px-3 py-1 font-semibold text-accent-fg text-xs shadow-lg">
          Showing B · press \ to return to A
        </div>
      )}
      {hover && (
        <div
          className="pointer-events-none absolute z-40 flex items-center gap-2 rounded-md border border-line bg-raised px-2 py-1 text-xs shadow-xl"
          style={{ left: hover.x, top: hover.y }}
        >
          <span
            className="size-3 rounded-sm ring-1 ring-black/30"
            style={{ background: hexOf(resolved, hover.id as TokenId) }}
          />
          <span>{labelFor(hover.id)}</span>
          <span className="font-mono text-muted">{hexOf(resolved, hover.id as TokenId)}</span>
        </div>
      )}
    </div>
  );
}
